import type { PortableTextBlock } from 'sanity';
import type {
  IAffiliateDiscountSettings,
  IPricingV2Discount,
} from '@/sanity/templates';
import type { IPricingSectionV3 } from '@/sanity/lib/definitions';
import { getNewOfferPercent } from '@/lib/affiliateNewOffer';

export type AffiliateFundingType = 'futures' | 'crypto';

export interface IAffiliateDiscountOverrides {
  isActive: boolean;
  challenge?: number;
  instantFunding?: number;
  max?: number;
  // "Up to X%" with this number is the profit split, not a discount.
  profitSplit?: number;
}

/**
 * Core resolution rule: the global affiliate override wins only when the
 * toggle is on AND the specific field is filled; otherwise the value already
 * stored on the page document stays in place.
 */
export function resolveAffiliateDiscount<T>(
  pageValue: T,
  affiliateSettingsValue: number | null | undefined,
  isActive: boolean | null | undefined,
): T | number {
  if (
    isActive &&
    typeof affiliateSettingsValue === 'number' &&
    affiliateSettingsValue > 0
  ) {
    return affiliateSettingsValue;
  }
  return pageValue;
}

// Empty and 0 both mean "not set" (same convention as Discount Settings).
function percentOrUndefined(value: number | null | undefined): number | undefined {
  return typeof value === 'number' && value > 0 ? value : undefined;
}

/**
 * The override is live when the master switch is on AND the optional end
 * date has not passed (same auto-hide semantics as the Summer Sale banner:
 * an empty date is no constraint, the end date passing deactivates without
 * flipping the switch).
 */
export function isAffiliateDiscountActive(
  settings: IAffiliateDiscountSettings | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!settings?.activateDiscountForAffiliates) return false;
  if (settings.endDate) {
    const end = new Date(settings.endDate).getTime();
    if (Number.isFinite(end) && end - now <= 0) return false;
  }
  return true;
}

export function getAffiliateDiscountOverrides(
  settings: IAffiliateDiscountSettings | null | undefined,
  fundingType: AffiliateFundingType,
): IAffiliateDiscountOverrides {
  const isActive = isAffiliateDiscountActive(settings);
  if (!isActive || !settings) return { isActive: false };
  return {
    isActive,
    challenge: percentOrUndefined(
      fundingType === 'futures'
        ? settings.futuresChallengeDiscountPercent
        : settings.cryptoChallengeDiscountPercent,
    ),
    instantFunding: percentOrUndefined(
      fundingType === 'futures'
        ? settings.futuresInstantFundingDiscountPercent
        : settings.cryptoInstantFundingDiscountPercent,
    ),
    max: percentOrUndefined(settings.maxDiscountPercent),
  };
}

// The offer copy reads its discount from `pricingV2Discount`, so it is rewritten there.
export function withAffiliateOfferDiscount<
  T extends {
    affiliateDiscountSettings?: IAffiliateDiscountSettings;
    pricingV2Discount?: IPricingV2Discount;
    pricingSectionV3?: IPricingSectionV3;
    pricingSectionV2AssetKey?: 'cfd_crypto' | 'futures' | 'both';
  },
>(data: T): T {
  const settings = data?.affiliateDiscountSettings;
  const pricingV2 = data?.pricingV2Discount;
  const pricingV3 = data?.pricingSectionV3;
  const isActive = isAffiliateDiscountActive(settings);
  const standard = percentOrUndefined(pricingV3?.discountPercent);
  const pick = (affiliate?: number, fallback?: number) =>
    (isActive ? percentOrUndefined(affiliate) : undefined) ?? standard ?? fallback;

  const pricingV2Discount: IPricingV2Discount = {
    futuresCoreDiscountPercent: pick(
      settings?.futuresChallengeDiscountPercent,
      pricingV2?.futuresCoreDiscountPercent,
    ),
    futuresInstantFundingDiscountPercent: pick(
      settings?.futuresInstantFundingDiscountPercent,
      pricingV2?.futuresInstantFundingDiscountPercent,
    ),
    cfdCryptoCoreDiscountPercent: pick(
      settings?.cryptoChallengeDiscountPercent,
      pricingV2?.cfdCryptoCoreDiscountPercent,
    ),
    cfdCryptoInstantFundingDiscountPercent: pick(
      settings?.cryptoInstantFundingDiscountPercent,
      pricingV2?.cfdCryptoInstantFundingDiscountPercent,
    ),
  };

  const assetKey = data?.pricingSectionV2AssetKey;
  const shownAssets: AffiliateFundingType[] =
    assetKey === 'futures'
      ? ['futures']
      : assetKey === 'cfd_crypto'
        ? ['crypto']
        : ['futures', 'crypto'];
  const cardPercents = shownAssets
    .map((fundingType) => getNewOfferPercent(pricingV2Discount, fundingType))
    .filter((value): value is number => value != null);

  return {
    ...data,
    pricingV2Discount,
    pricingSectionV3:
      isActive && pricingV3 && cardPercents.length
        ? { ...pricingV3, discountPercent: Math.min(...cardPercents) }
        : pricingV3,
  };
}

// The page copy embeds the percentages inside text, e.g.
// "80 % Rabatt auf Challenges", "40% auf Instant Funding",
// "70% off Challenges", "bis zu 80% sparen", "save up to 70%".
// Only the number is swapped; the surrounding copy stays untouched.
const CHALLENGE_RE =
  /(\d+(?:[.,]\d+)?)(\s*%\s*(?:Rabatt\s+)?(?:auf|off|on)\s+(?:alle\s+|all\s+)?Challenges?)/gi;
const INSTANT_FUNDING_RE =
  /(\d+(?:[.,]\d+)?)(\s*%\s*(?:Rabatt\s+)?(?:auf|off|on)\s+Instant[\s-]?Funding)/gi;
const MAX_RE = /((?:bis\s+zu|up\s+to)\s+)(\d+(?:[.,]\d+)?)(\s*%)/gi;
const MAX_SPAREN_RE = /(\d+(?:[.,]\d+)?)(\s*%\s*sparen)/gi;
// For fields that are pure discount labels (deal card chip/callout, e.g.
// "80% VIP Deal", "40 % Rabatt sichern") where the funding type is known
// from the card itself.
const ANY_PERCENT_RE = /(\d+(?:[.,]\d+)?)(\s*%)/g;

export function applyAffiliateDiscountToText<T extends string | undefined | null>(
  text: T,
  overrides: IAffiliateDiscountOverrides,
): T {
  if (!overrides.isActive || !text) return text;
  let out: string = text;
  if (overrides.challenge != null) {
    out = out.replace(CHALLENGE_RE, `${overrides.challenge}$2`);
  }
  if (overrides.instantFunding != null) {
    out = out.replace(INSTANT_FUNDING_RE, `${overrides.instantFunding}$2`);
  }
  if (overrides.max != null) {
    const { max, profitSplit } = overrides;
    out = out.replace(MAX_RE, (match, upTo: string, value: string, percent: string) =>
      profitSplit != null && Number(value.replace(',', '.')) === profitSplit
        ? match
        : `${upTo}${max}${percent}`,
    );
    out = out.replace(MAX_SPAREN_RE, `${overrides.max}$2`);
  }
  return out as T;
}

export function applyAffiliateDiscountToRichtext<
  T extends PortableTextBlock[] | undefined | null,
>(blocks: T, overrides: IAffiliateDiscountOverrides): T {
  if (!overrides.isActive || !Array.isArray(blocks)) return blocks;
  return blocks.map((block) => {
    if (!block || block._type !== 'block' || !Array.isArray(block.children)) {
      return block;
    }
    return {
      ...block,
      children: block.children.map((child: any) =>
        child && typeof child.text === 'string'
          ? { ...child, text: applyAffiliateDiscountToText(child.text, overrides) }
          : child,
      ),
    };
  }) as T;
}

/**
 * For discount-only labels (deal card chip/callout) where the surrounding
 * text carries no funding-type keyword — the caller passes the value that
 * applies to that card.
 */
export function applyAffiliateDiscountToLabel<
  T extends string | undefined | null,
>(text: T, value: number | null | undefined): T {
  if (!text || value == null) return text;
  return text.replace(ANY_PERCENT_RE, `${value}$2`) as T;
}

// ─── Deal pricing tables (Community-Deals) ──────────────────────────────────
// Each table stores columns; the discounted-price column ("VIP Preis (-80%)" /
// "Preis mit 40% Rabatt") is computed per row from the regular-price column:
//   discountedPrice = regularPrice * (1 - discountPercent / 100)
// The percent in the column header is the page-level fallback; the global
// Affiliate Discount Settings value wins when the toggle is active. A row's
// `discountedPriceOverride` (e.g. the "$1" Start-Angebot row) always renders
// verbatim.
// Exception: on crypto/CFD tables the $10,000 starter challenge is a fixed
// offer (the "10k just $1" Start-Angebot). Its row keeps whatever price is
// stored in Sanity — no sale percentage is ever applied to it.

interface IDealTableRow {
  value: PortableTextBlock[];
  discountedPriceOverride?: string;
}

interface IDealTable {
  tableItem: Array<{
    title: string;
    rows: Array<IDealTableRow>;
  }>;
}

const REGULAR_PRICE_COLUMN_RE = /regul/i; // "Regulärer Preis", "Regulärer preis/monat"
const DISCOUNT_COLUMN_RE = /vip\s*preis|rabatt/i; // "VIP Preis (-80%)", "Preis mit 40% Rabatt"
const ACCOUNT_SIZE_COLUMN_RE = /account\s*size/i; // "Account Size", "Account<br/>Size"
const PERCENT_VALUE_RE = /(\d+(?:[.,]\d+)?)(\s*%)/;
// Starter challenge sold at a fixed price on crypto/CFD pages — never discounted.
const STATIC_PRICE_ACCOUNT_SIZE = 10000;

function stripHtml(text: string): string {
  return text.replace(/<[^>]*>/g, ' ');
}

function richtextToPlainText(blocks: PortableTextBlock[] | undefined | null): string {
  if (!Array.isArray(blocks)) return '';
  return blocks
    .map((block: any) =>
      Array.isArray(block?.children)
        ? block.children.map((child: any) => child?.text ?? '').join('')
        : '',
    )
    .join('\n');
}

function parseDollarPrice(text: string): number | undefined {
  const match = text.replace(/ /g, ' ').match(/\$\s*([\d,]+(?:\.\d+)?)/);
  if (!match) return undefined;
  const value = parseFloat(match[1].replace(/,/g, ''));
  return Number.isFinite(value) ? value : undefined;
}

function formatDollarPrice(value: number): string {
  return (
    '$' +
    value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

/**
 * Deal cards that store both prices as plain strings (the Fabio/WCE community
 * deals) instead of a table: the discounted price is recomputed from the
 * regular one so it can never drift from the advertised percentage. Returns
 * undefined when the regular price cannot be parsed, so the caller keeps the
 * stored value.
 */
export function discountedPriceFrom(
  originalPrice: string | undefined | null,
  percent: number | undefined,
): string | undefined {
  if (!originalPrice || percent == null) return undefined;
  const original = parseDollarPrice(originalPrice);
  if (original == null) return undefined;
  return formatDollarPrice(original * (1 - percent / 100));
}

function plainTextCell(text: string, key: string): PortableTextBlock[] {
  return [
    {
      _type: 'block',
      _key: `calc-price-${key}`,
      style: 'normal',
      markDefs: [],
      children: [
        {
          _type: 'span',
          _key: `calc-price-span-${key}`,
          text,
          marks: [],
        },
      ],
    } as unknown as PortableTextBlock,
  ];
}

/**
 * Recomputes the discounted-price column of a deal table. Runs regardless of
 * the toggle: the header percent is the fallback source, the global settings
 * value overrides it when active (via resolveAffiliateDiscount). Rows whose
 * regular price cannot be parsed keep their stored value.
 */
export function transformAffiliateDealTable<T extends IDealTable | undefined | null>(
  table: T,
  settingsPercent: number | null | undefined,
  isActive: boolean | null | undefined,
  fundingType: AffiliateFundingType,
): T {
  if (!table?.tableItem?.length) return table;

  const columns = table.tableItem;
  const discountIndex = columns.findIndex(
    (column) =>
      DISCOUNT_COLUMN_RE.test(stripHtml(column.title ?? '')) &&
      PERCENT_VALUE_RE.test(stripHtml(column.title ?? '')),
  );
  const regularIndex = columns.findIndex((column) =>
    REGULAR_PRICE_COLUMN_RE.test(stripHtml(column.title ?? '')),
  );
  if (discountIndex < 0 || regularIndex < 0 || discountIndex === regularIndex) {
    return table;
  }

  const discountColumn = columns[discountIndex];
  const regularColumn = columns[regularIndex];
  const accountSizeColumn = columns.find((column) =>
    ACCOUNT_SIZE_COLUMN_RE.test(stripHtml(column.title ?? '')),
  );

  // The $10,000 crypto/CFD starter row is a fixed offer: it renders the price
  // stored in Sanity instead of one derived from the current sale percentage.
  const hasStaticPrice = (rowIndex: number) =>
    fundingType === 'crypto' &&
    parseDollarPrice(
      richtextToPlainText(accountSizeColumn?.rows?.[rowIndex]?.value),
    ) === STATIC_PRICE_ACCOUNT_SIZE;

  const headerMatch = stripHtml(discountColumn.title ?? '').match(PERCENT_VALUE_RE);
  const fallbackPercent = headerMatch
    ? parseFloat(headerMatch[1].replace(',', '.'))
    : undefined;
  const percent = resolveAffiliateDiscount(fallbackPercent, settingsPercent, isActive);
  if (typeof percent !== 'number' || !(percent > 0) || percent >= 100) {
    return table;
  }

  const title = discountColumn.title
    ? discountColumn.title.replace(PERCENT_VALUE_RE, `${percent}$2`)
    : discountColumn.title;

  const rows = discountColumn.rows?.map((row, rowIndex) => {
    if (row?.discountedPriceOverride) {
      return { ...row, value: plainTextCell(row.discountedPriceOverride, `${rowIndex}`) };
    }
    if (hasStaticPrice(rowIndex)) return row;
    const regularPrice = parseDollarPrice(
      richtextToPlainText(regularColumn.rows?.[rowIndex]?.value),
    );
    if (regularPrice == null) return row;
    const discounted = Math.round(regularPrice * (100 - percent)) / 100;
    return { ...row, value: plainTextCell(formatDollarPrice(discounted), `${rowIndex}`) };
  });

  return {
    ...table,
    tableItem: columns.map((column, index) =>
      index === discountIndex ? { ...column, title, rows } : column,
    ),
  } as T;
}
